/**
 * ViewModeToggle — reusable Card / List switcher.
 *
 * - `useViewMode(moduleKey)` — per-module preference (persisted at `pk:vm:<key>`).
 * - `useGlobalViewMode()` — single global preference (persisted at `pk:vm:global`).
 *   Any page can subscribe to the global toggle; changes made in the top bar
 *   broadcast instantly via the storage event.
 *
 * Usage in top bar:
 *   const [mode, setMode] = useGlobalViewMode();
 *   <ViewModeToggle mode={mode} onChange={setMode} testid="global-view-mode" />
 *
 * Usage on a page:
 *   const [mode] = useGlobalViewMode();
 *   {mode === "card" ? <CardsGrid /> : <ListTable />}
 */
import { useCallback, useEffect, useState } from "react";
import { LayoutGrid, List } from "lucide-react";

const GLOBAL_VM_EVENT = "pk-vm-change";

function readVM(key, def) {
  try { return localStorage.getItem(key) || def; } catch { return def; }
}
function writeVM(key, val) {
  try { localStorage.setItem(key, val); } catch { /* silent */ }
  try { window.dispatchEvent(new CustomEvent(GLOBAL_VM_EVENT, { detail: { key, val } })); } catch { /* silent */ }
}

export function useViewMode(moduleKey, defaultMode = "card") {
  const storageKey = `pk:vm:${moduleKey}`;
  const [mode, setModeState] = useState(() => readVM(storageKey, defaultMode));
  const setMode = useCallback((m) => {
    setModeState(m);
    writeVM(storageKey, m);
  }, [storageKey]);
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === storageKey && e.newValue) setModeState(e.newValue);
    };
    const onLocal = (e) => {
      if (e?.detail?.key === storageKey) setModeState(e.detail.val);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(GLOBAL_VM_EVENT, onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(GLOBAL_VM_EVENT, onLocal);
    };
  }, [storageKey]);
  return [mode, setMode];
}

// Convenience hook for the global card/list preference.
export function useGlobalViewMode(defaultMode = "card") {
  return useViewMode("global", defaultMode);
}

export default function ViewModeToggle({ mode, onChange, testid = "view-mode-toggle", compact = false }) {
  const isCard = mode === "card";
  const btnSize = compact ? "w-9 h-9" : "w-10 h-10";
  const iconSize = compact ? 16 : 18;
  const toggle = () => onChange(isCard ? "list" : "card");
  return (
    <button
      type="button"
      onClick={toggle}
      role="switch"
      aria-checked={isCard}
      aria-label={isCard ? "Switch to List view" : "Switch to Card view"}
      title={isCard ? "Card view · click for List" : "List view · click for Card"}
      className={`relative ${btnSize} rounded-full grid place-items-center overflow-hidden transition-colors duration-500 ${
        isCard
          ? "bg-indigo-100 hover:bg-indigo-200 text-indigo-700 dark:bg-indigo-500/20 dark:hover:bg-indigo-500/30 dark:text-indigo-200"
          : "bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200"
      }`}
      data-testid={testid}
      data-mode={mode}
    >
      {/* Lines — shown in List mode. Fades + rotates + shrinks when Card is engaged. */}
      <List
        size={iconSize}
        strokeWidth={2.4}
        data-keep-color=""
        data-testid={`${testid}-list`}
        className={`absolute transition-all duration-500 ease-[cubic-bezier(.65,.05,.36,1)] ${
          isCard ? "opacity-0 rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"
        }`}
      />
      {/* Box (Card) — shown in Card mode. Fades + rotates in when Card is engaged. */}
      <LayoutGrid
        size={iconSize}
        strokeWidth={2.4}
        data-keep-color=""
        data-testid={`${testid}-card`}
        className={`absolute transition-all duration-500 ease-[cubic-bezier(.65,.05,.36,1)] ${
          isCard ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-50"
        }`}
      />
    </button>
  );
}
